/**
 * Map Repair Engine v1 — orchestrator.
 *
 * Flow:
 *   1. Advisory lock (try_advisory_audit_lock) — nie może jednocześnie audit + repair
 *   2. Fetch state: nodes, seeds, relations, audit_report.layers.{L2_deterministic, L5_coverage}
 *   3. Compute incomingByNodeId
 *   4. Run rules R1..R7 (pure functions) → operations[], proposals[], flags[], skipped[]
 *   5. Dla R3/R6 — filter proposals po accepted IDs z opts
 *   6. Jeśli dry_run=false → atomic apply (rewire → move → delete → seed demote) + INSERT map_repair_log
 *   7. Mark map: repair_status='completed' + last_repaired_at + rollback_available_until
 *   8. Trigger auditMapFull({skipLLM:true}) → compute re_audit_delta
 *   9. UPDATE thematic_maps.repair_report = RepairReport
 *
 * Atomicity: Supabase JS nie ma wbudowanych transakcji REST. Używamy pattern'u z
 * existingPostMatcher.ts: poszczególne statement-y + bezpieczna kolejność (rewire
 * relations PRZED delete nodes, żeby nie osieroceć). Errory → repair_status='failed'.
 */

import { supabase } from "@/integrations/supabase/client";
import { auditMapFull } from "@/services/validation/mapAuditOrchestrator";
import { repairLog } from "@/services/contentEngine/contentLogger";
const logPublic = repairLog.public;
const logDebug = repairLog.debug;
const logError = repairLog.error;
import type {
  RepairReport,
  RepairMapOptions,
  RepairMapResult,
  RepairRuleInput,
  RepairRuleResult,
  RepairNodeRow,
  RepairSeedRow,
  RepairRelationRow,
  MergeOp,
  MoveProposal,
  RelationProposal,
  DemotedSeed,
  BrokenRelationRef,
} from "@/types/mapRepair";
import { runR1 } from "./rules/r1_sameSeedDuplicates";
import { runR2 } from "./rules/r2_crossSeedDuplicates";
import { runR3 } from "./rules/r3_misplacement";
import { runR4 } from "./rules/r4_brokenRelations";
import { runR5 } from "./rules/r5_slugTypos";
import { runR6 } from "./rules/r6_orphanAutoLink";
import { runR7 } from "./rules/r7_ghostSeeds";

const PREFIX = "[MAP-REPAIR]";
const ROLLBACK_WINDOW_HOURS = 24;

type LogCb = NonNullable<RepairMapOptions["onLog"]>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

async function fetchMapState(
  mapId: string,
): Promise<{
  nodes: RepairNodeRow[];
  seeds: RepairSeedRow[];
  relations: RepairRelationRow[];
  auditReport: RepairRuleInput["auditReport"];
  scoreBefore: number | null;
  l2DetBefore: number | null;
  crossSeedBridgesBefore: number | null;
}> {
  const [nodesRes, seedsRes, relsRes, mapRes] = await Promise.all([
    db
      .from("thematic_nodes")
      .select(
        "id, slug, seed_id, primary_entity_raw, primary_intent, search_volume, matched_wp_post_id, node_role, document_role",
      )
      .eq("map_id", mapId),
    db
      .from("thematic_seeds")
      .select("id, name, priority, status, map_id")
      .eq("map_id", mapId),
    db
      .from("thematic_node_relations")
      .select("id, from_node_id, to_node_id, relation_type, weight, map_id")
      .eq("map_id", mapId),
    db
      .from("thematic_maps")
      .select("audit_report, audit_score")
      .eq("id", mapId)
      .maybeSingle(),
  ]);

  if (nodesRes.error) throw nodesRes.error;
  if (seedsRes.error) throw seedsRes.error;
  if (relsRes.error) throw relsRes.error;
  if (mapRes.error) throw mapRes.error;

  const nodes = (nodesRes.data || []) as RepairNodeRow[];
  const seeds = (seedsRes.data || []) as RepairSeedRow[];
  const relations = (relsRes.data || []) as RepairRelationRow[];

  const auditReport = mapRes.data?.audit_report || {};
  const layers = auditReport.layers || {};
  const l2Det = layers.L2_deterministic || {};
  const l5 = layers.L5_coverage || {};

  return {
    nodes,
    seeds,
    relations,
    auditReport: {
      duplicate_groups: l2Det.duplicate_groups,
      ghost_seeds: l5.ghost_seeds,
    },
    scoreBefore: auditReport.meta?.total_score ?? null,
    l2DetBefore: l2Det.score ?? null,
    crossSeedBridgesBefore: l2Det.cross_seed_bridges ?? null,
  };
}

function computeIncoming(
  relations: RepairRelationRow[],
): Map<string, number> {
  const incoming = new Map<string, number>();
  for (const r of relations) {
    incoming.set(r.to_node_id, (incoming.get(r.to_node_id) ?? 0) + 1);
  }
  return incoming;
}

/**
 * Symuluje apply R1 w pamięci (pure) i zwraca nowy snapshot stanu.
 * Używane żeby R2 operował na stanie PO R1 (nie na oryginalnym snapshot) —
 * zapobiega FK violations gdy R2 canonical = node usunięty przez R1, albo gdy
 * R2 chce rewire relację którą R1 już usunął/zrewirował.
 *
 * Transformacje:
 *   - nodes: usuń te co R1 delete (merge.deleted[])
 *   - relations:
 *     - usuń te w R1 self_loops + redundant_duplicates + CASCADE po deleted nodes
 *     - rewire from/to dla relacji oznaczonych w R1 rewire[]
 *   - incomingByNodeId: recompute od nowa
 */
function computeStateAfterR1(
  input: RepairRuleInput,
  r1: RepairRuleResult,
): RepairRuleInput {
  const deletedNodeIds = new Set<string>();
  const deletedRelationIds = new Set<string>();
  const relationRewireMap = new Map<
    string,
    { new_from: string; new_to: string }
  >();

  for (const op of r1.operations) {
    if (op.operation !== "merge") continue;
    const merge = op.payload.merge as MergeOp;
    for (const d of merge.deleted) deletedNodeIds.add(d.id);
    for (const id of (op.payload.self_loops as string[]) || []) {
      deletedRelationIds.add(id);
    }
    for (const id of (op.payload.redundant_duplicates as string[]) || []) {
      deletedRelationIds.add(id);
    }
    for (const rew of (op.payload.rewire as Array<{
      relation_id: string;
      new_from: string;
      new_to: string;
    }>) || []) {
      relationRewireMap.set(rew.relation_id, {
        new_from: rew.new_from,
        new_to: rew.new_to,
      });
    }
  }

  const nodes = input.nodes.filter((n) => !deletedNodeIds.has(n.id));

  const relations = input.relations
    .filter((r) => {
      if (deletedRelationIds.has(r.id)) return false;
      // CASCADE: relacje których from/to to deleted node (i NIE są rewire'owane) znikną
      if (
        !relationRewireMap.has(r.id) &&
        (deletedNodeIds.has(r.from_node_id) || deletedNodeIds.has(r.to_node_id))
      ) {
        return false;
      }
      return true;
    })
    .map((r) => {
      const rewire = relationRewireMap.get(r.id);
      if (rewire) {
        return {
          ...r,
          from_node_id: rewire.new_from,
          to_node_id: rewire.new_to,
        };
      }
      return r;
    });

  const incomingByNodeId = computeIncoming(relations);

  return {
    ...input,
    nodes,
    relations,
    incomingByNodeId,
  };
}

/**
 * Apply pojedynczego rule result w DB (tylko operations[], nie proposals/flags).
 * Atomic per operation — jak coś pójdzie nie tak, orchestrator i tak ustawia
 * repair_status='failed' na końcu.
 */
async function applyOperation(
  mapId: string,
  ruleResult: RepairRuleResult,
  op: RepairRuleResult["operations"][number],
  dryRun: boolean,
): Promise<void> {
  if (dryRun) {
    // Tylko log do map_repair_log, bez mutacji DB
    await db.from("map_repair_log").insert({
      map_id: mapId,
      rule_id: ruleResult.rule,
      operation: op.operation,
      dry_run: true,
      payload: op.payload,
      rollback_payload: null,
      user_confirmed: null,
    });
    return;
  }

  // R1/R2 merge — KOLEJNOŚĆ KRYTYCZNA dla constraints:
  //   no_self_relation CHECK (from != to) + uq_relation UNIQUE (map_id, from, to, type)
  //
  //   1. DELETE self-loops + redundant duplicates PIERWSZE (zwalnia miejsce w UNIQUE)
  //   2. UPDATE rewire (teraz się nie zderza bo duplikaty usunięte)
  //   3. (R2 only) UPDATE canonical.seed_id
  //   4. DELETE duplicate nodes (cascaded relations już przepisane/usunięte)
  if (op.operation === "merge") {
    const merge = op.payload.merge as MergeOp;
    const rewire = (op.payload.rewire || []) as Array<{
      relation_id: string;
      new_from: string;
      new_to: string;
    }>;
    const selfLoops = (op.payload.self_loops || []) as string[];
    const redundantDuplicates = (op.payload.redundant_duplicates ||
      []) as string[];
    const move = op.payload.move as {
      node_id: string;
      new_seed_id: string;
    } | null;

    // 1. DELETE najpierw (self-loops + duplicates które byłyby UNIQUE violation)
    const toDelete = [...selfLoops, ...redundantDuplicates];
    if (toDelete.length > 0) {
      const { error: delErr } = await db
        .from("thematic_node_relations")
        .delete()
        .in("id", toDelete);
      if (delErr) throw new Error(`R1/R2 cleanup delete: ${delErr.message}`);
    }

    // 2. UPDATE rewire pojedynczo — per relation z error handling
    // (jeśli mimo dedup pre-check nadal zderzy się z UNIQUE, logujemy i DELETE'ujemy
    // tę jedną relację zamiast wysypać cały repair)
    for (const r of rewire) {
      const { error: updErr } = await db
        .from("thematic_node_relations")
        .update({ from_node_id: r.new_from, to_node_id: r.new_to })
        .eq("id", r.relation_id);
      if (updErr) {
        const msg = updErr.message || String(updErr);
        if (
          msg.includes("uq_relation") ||
          msg.includes("duplicate key") ||
          msg.includes("no_self_relation") ||
          msg.includes("violates check constraint")
        ) {
          // Nieoczekiwany conflict — usuń relację bo inna już pokrywa ten link
          await db
            .from("thematic_node_relations")
            .delete()
            .eq("id", r.relation_id);
        } else {
          throw new Error(`R1/R2 rewire ${r.relation_id}: ${msg}`);
        }
      }
    }

    // 3. R2 only — move canonical do target seed
    if (move) {
      const { error: moveErr } = await db
        .from("thematic_nodes")
        .update({ seed_id: move.new_seed_id })
        .eq("id", move.node_id);
      if (moveErr) throw new Error(`R2 move: ${moveErr.message}`);
    }

    // 4. DELETE duplicate nodes (guard: tylko te bez matched_wp_post)
    const deletedIds = merge.deleted.map((d) => d.id);
    if (deletedIds.length > 0) {
      const { error: nodeDelErr } = await db
        .from("thematic_nodes")
        .delete()
        .in("id", deletedIds)
        .is("matched_wp_post_id", null);
      if (nodeDelErr) throw new Error(`R1/R2 node delete: ${nodeDelErr.message}`);
    }
  }

  // R4 — delete broken relations
  if (op.operation === "delete" && op.payload.rule === "R4") {
    const ids = (op.payload.relation_ids || []) as string[];
    if (ids.length > 0) {
      await db.from("thematic_node_relations").delete().in("id", ids);
    }
  }

  // R7 — seed demote
  if (op.operation === "demote" && op.payload.rule === "R7") {
    await db
      .from("thematic_seeds")
      .update({ status: op.payload.new_status })
      .eq("id", op.payload.seed_id);
  }

  // Accepted R3 move (user-confirmed)
  if (op.operation === "move" && op.payload.rule === "R3") {
    await db
      .from("thematic_nodes")
      .update({ seed_id: op.payload.new_seed_id })
      .eq("id", op.payload.node_id);
  }

  // Accepted R6 link (user-confirmed) — INSERT relation
  if (op.operation === "link" && op.payload.rule === "R6") {
    await db.from("thematic_node_relations").insert({
      map_id: mapId,
      from_node_id: op.payload.from_node_id,
      to_node_id: op.payload.to_node_id,
      relation_type: op.payload.relation_type || "sibling",
      weight: op.payload.weight ?? 5,
    });
  }

  // Log do map_repair_log
  await db.from("map_repair_log").insert({
    map_id: mapId,
    rule_id: ruleResult.rule,
    operation: op.operation,
    dry_run: false,
    payload: op.payload,
    rollback_payload: op.rollback_payload,
    user_confirmed:
      op.payload.rule === "R3" || op.payload.rule === "R6" ? true : null,
  });
}

/**
 * Build extra operations from accepted R3/R6 proposals.
 * User-confirmed z UI (checkbox per propozycja).
 */
function proposalsToOperations(
  r3Proposals: MoveProposal[],
  r6Proposals: RelationProposal[],
  acceptedR3: string[],
  acceptedR6: string[],
): {
  r3Ops: RepairRuleResult["operations"];
  r6Ops: RepairRuleResult["operations"];
  acceptedR3Props: MoveProposal[];
  acceptedR6Props: RelationProposal[];
} {
  const r3Set = new Set(acceptedR3);
  const r6Set = new Set(acceptedR6);

  const acceptedR3Props = r3Proposals.filter((p) => r3Set.has(p.node_id));
  const acceptedR6Props = r6Proposals.filter((p) =>
    r6Set.has(`${p.from_node_id}::${p.to_node_id}`),
  );

  const r3Ops: RepairRuleResult["operations"] = acceptedR3Props.map((p) => ({
    operation: "move",
    payload: {
      rule: "R3",
      node_id: p.node_id,
      node_slug: p.node_slug,
      old_seed_id: p.current_seed_id,
      new_seed_id: p.proposed_seed_id,
      confidence: p.confidence,
    },
    rollback_payload: {
      node_id: p.node_id,
      old_seed_id: p.current_seed_id,
    },
  }));

  const r6Ops: RepairRuleResult["operations"] = acceptedR6Props.map((p) => ({
    operation: "link",
    payload: {
      rule: "R6",
      from_node_id: p.from_node_id,
      to_node_id: p.to_node_id,
      relation_type: p.relation_type,
      weight: p.weight,
      confidence: p.confidence,
    },
    rollback_payload: {
      from_node_id: p.from_node_id,
      to_node_id: p.to_node_id,
    },
  }));

  return { r3Ops, r6Ops, acceptedR3Props, acceptedR6Props };
}

function collectR7Demoted(r7: RepairRuleResult): DemotedSeed[] {
  return r7.operations
    .filter((op) => op.operation === "demote")
    .map((op) => ({
      seed_id: op.payload.seed_id as string,
      seed_name: op.payload.seed_name as string,
      total_nodes: 0, // uzupełniamy z rollback payload lub z ghostSeeds metadata
      old_status: (op.rollback_payload?.old_status as "active") || "active",
    }));
}

function collectR4Deleted(
  r4: RepairRuleResult,
): { deleted_count: number; sample: BrokenRelationRef[] } {
  const op = r4.operations[0];
  if (!op) return { deleted_count: 0, sample: [] };
  return {
    deleted_count: (op.payload.deleted_count as number) || 0,
    sample: (op.payload.sample as BrokenRelationRef[]) || [],
  };
}

export async function repairMap(
  mapId: string,
  opts: RepairMapOptions = {},
): Promise<RepairMapResult> {
  const t0 = Date.now();
  const dryRun = opts.dryRun ?? true;
  const onLog: LogCb = opts.onLog || (() => {});

  onLog("stage", `▶ Map Repair v1 start (dryRun=${dryRun})`);
  logPublic(PREFIX, `repair start mapId=${mapId} dryRun=${dryRun}`);

  // 1. Advisory lock
  try {
    const { data: lockRow, error: lockErr } = await db.rpc(
      "try_advisory_audit_lock",
      { p_map_id: mapId },
    );
    if (lockErr) {
      logDebug(PREFIX, `advisory lock RPC error, proceeding: ${lockErr.message}`);
    } else if (lockRow === false) {
      onLog("error", "Repair pominięty — inny audit/repair trwa dla tej mapy");
      return { success: true, mapId, skipped_by_lock: true } as RepairMapResult;
    }
  } catch (e) {
    logDebug(PREFIX, `lock check error, proceeding: ${(e as Error).message}`);
  }

  // 2. Mark in_progress
  if (!dryRun) {
    await db
      .from("thematic_maps")
      .update({ repair_status: "in_progress" })
      .eq("id", mapId);
  }

  try {
    // 3. Fetch state
    onLog("info", "Pobieranie stanu mapy...");
    const state = await fetchMapState(mapId);
    onLog(
      "info",
      `  nodes=${state.nodes.length} seeds=${state.seeds.length} relations=${state.relations.length}`,
    );

    const incoming = computeIncoming(state.relations);
    const ruleInput: RepairRuleInput = {
      mapId,
      nodes: state.nodes,
      seeds: state.seeds,
      relations: state.relations,
      auditReport: state.auditReport,
      incomingByNodeId: incoming,
    };

    // 4. Run rules
    //    KRYTYCZNE: R2 musi dostać stan PO R1 (symulowany w pamięci przez
    //    computeStateAfterR1), żeby nie wybrać canonical = node usunięty przez
    //    R1 + nie próbować rewire relacji którą R1 już usunął/zrewirował.
    //    Bez tego FK violations na thematic_node_relations_from/to_node_id_fkey.
    onLog("stage", "▶ R1 same-seed duplicates");
    const r1 = runR1(ruleInput);
    onLog(
      "info",
      `  R1: ${r1.operations.length} merges, ${r1.skipped.length} skipped`,
    );

    const r2Input = computeStateAfterR1(ruleInput, r1);
    onLog(
      "info",
      `  Post-R1 state: nodes=${r2Input.nodes.length} (z ${ruleInput.nodes.length}) relations=${r2Input.relations.length} (z ${ruleInput.relations.length})`,
    );

    onLog("stage", "▶ R2 cross-seed duplicates (na stanie po R1)");
    const r2 = runR2(r2Input);
    onLog(
      "info",
      `  R2: ${r2.operations.length} merges, ${r2.skipped.length} skipped`,
    );

    onLog("stage", "▶ R3 misplacement (review queue)");
    const r3 = runR3(ruleInput);
    onLog("info", `  R3: ${r3.proposals.length} proposed moves`);

    onLog("stage", "▶ R4 broken relations");
    const r4 = runR4(ruleInput);
    onLog(
      "info",
      `  R4: ${collectR4Deleted(r4).deleted_count} broken relations to clean`,
    );

    onLog("stage", "▶ R5 slug typo detection");
    const r5 = runR5(ruleInput);
    onLog("info", `  R5: ${r5.flags.length} flagged slugs`);

    onLog("stage", "▶ R6 orphan auto-link (review queue)");
    const r6 = runR6(ruleInput);
    onLog("info", `  R6: ${r6.proposals.length} proposed relations`);

    onLog("stage", "▶ R7 ghost seed demotion");
    const r7 = runR7(ruleInput);
    onLog("info", `  R7: ${r7.operations.length} seeds to demote`);

    // 5. Accepted R3/R6 → operations
    const { r3Ops, r6Ops, acceptedR3Props, acceptedR6Props } =
      proposalsToOperations(
        r3.proposals as MoveProposal[],
        r6.proposals as RelationProposal[],
        opts.acceptedR3 || [],
        opts.acceptedR6 || [],
      );
    onLog(
      "info",
      `Accepted user proposals: R3=${r3Ops.length} R6=${r6Ops.length}`,
    );

    // 5.5. Pre-filter overlapping R1/R2 operations — KRYTYCZNE dla zero FK violations.
    //
    //    Bug: r1+r2 merges mogą mieć overlapping node sets:
    //      op1: canonical=A, deleted=[B]
    //      op2: canonical=B, deleted=[C]
    //    Apply op1 → B usunięte. Apply op2 → UPDATE SET to/from=B → FK violation.
    //
    //    Fix: jeśli canonical.id jakiejś operacji jest w deleted_ids INNEJ operacji, skip tę
    //    operację. Mapa dostaje mniej merges niż planowane, ale 0 FK errors, 0 race conditions.
    const allDeletedIds = new Set<string>();
    for (const op of r1.operations) {
      const merge = op.payload.merge as MergeOp;
      for (const d of merge.deleted) allDeletedIds.add(d.id);
    }
    for (const op of r2.operations) {
      const merge = op.payload.merge as MergeOp;
      for (const d of merge.deleted) allDeletedIds.add(d.id);
    }

    const safeR1 = r1.operations.filter((op) => {
      const canonicalId = (op.payload.merge as MergeOp).canonical.id;
      return !allDeletedIds.has(canonicalId);
    });
    const safeR2 = r2.operations.filter((op) => {
      const canonicalId = (op.payload.merge as MergeOp).canonical.id;
      return !allDeletedIds.has(canonicalId);
    });
    const r1Skipped = r1.operations.length - safeR1.length;
    const r2Skipped = r2.operations.length - safeR2.length;
    if (r1Skipped > 0 || r2Skipped > 0) {
      onLog(
        "info",
        `  Pre-filter: ${r1Skipped} R1 + ${r2Skipped} R2 ops skipped (canonical overlaps with another op's deletion)`,
      );
    }

    // 5.6. R7 safety check: gdy cała mapa ma 0 matched WP posts, NIE demotuj wszystkich seedów.
    //    Audit flaguje każdy seed z 0% matched jako "ghost", więc mapa bez WP coverage
    //    dostaje WSZYSTKIE seedy jako ghost — R7 zdemotowałby całą mapę do pending_production.
    const totalMatchedInMap = ruleInput.nodes.filter((n) => n.matched_wp_post_id).length;
    const safeR7 =
      totalMatchedInMap === 0
        ? (onLog(
            "info",
            `  R7 skipped: mapa ma 0 matched WP posts (wszystkie seedy byłyby demotowane, no-op)`,
          ),
          [])
        : r7.operations;

    // 6. Apply w kolejności bezpiecznej: R4 (cleanup) → R1 → R2 → R7 → R3 → R6
    // (R1/R2 robi rewire+delete; R7 UPDATE seeds; R3 UPDATE nodes; R6 INSERT relations)
    const allOps: Array<{
      rule: RepairRuleResult["rule"];
      op: RepairRuleResult["operations"][number];
    }> = [];
    for (const op of r4.operations) allOps.push({ rule: "R4", op });
    for (const op of safeR1) allOps.push({ rule: "R1", op });
    for (const op of safeR2) allOps.push({ rule: "R2", op });
    for (const op of safeR7) allOps.push({ rule: "R7", op });
    for (const op of r3Ops) allOps.push({ rule: "R3", op });
    for (const op of r6Ops) allOps.push({ rule: "R6", op });

    // In-memory alive tracking — po KAŻDEJ merge operation updujemy zbiór żyjących
    // nodów. Przed każdą merge pre-check przechodzi rewire plan: jeśli new_from
    // lub new_to nie jest alive, rewire konwertuje się na DELETE (dodajemy do
    // self_loops list, applyOperation i tak DELETE'uje self_loops pierwszy).
    // To eliminuje FK violations z race conditions między operacjami.
    const aliveNodeIds = new Set<string>(ruleInput.nodes.map((n) => n.id));
    let rewireConvertedToDelete = 0;

    onLog("stage", `▶ Apply ${allOps.length} operations (dryRun=${dryRun})`);
    let applied = 0;
    let failed = 0;
    const failedOps: Array<{ rule: string; error: string }> = [];
    for (const { rule, op } of allOps) {
      // Pre-check dla merge: przesuń niealive rewire ops do DELETE
      if (op.operation === "merge") {
        const rewire = (op.payload.rewire || []) as Array<{
          relation_id: string;
          new_from: string;
          new_to: string;
        }>;
        const safeRewire: typeof rewire = [];
        const extraSelfLoops: string[] = [];
        for (const r of rewire) {
          if (aliveNodeIds.has(r.new_from) && aliveNodeIds.has(r.new_to)) {
            safeRewire.push(r);
          } else {
            extraSelfLoops.push(r.relation_id);
            rewireConvertedToDelete++;
          }
        }
        if (extraSelfLoops.length > 0) {
          op.payload.rewire = safeRewire;
          op.payload.self_loops = [
            ...((op.payload.self_loops as string[]) || []),
            ...extraSelfLoops,
          ];
        }

        // Pre-check canonical: jeśli sam canonical już nie żyje (race w midst of R2 bunch),
        // skip operację całkowicie
        const merge = op.payload.merge as MergeOp;
        if (!aliveNodeIds.has(merge.canonical.id)) {
          onLog(
            "info",
            `  ⚠ ${rule} op skipped: canonical ${merge.canonical.slug} już nie żyje (usunięty przez poprzednią op)`,
          );
          continue;
        }
      }

      try {
        await applyOperation(
          mapId,
          { rule, operations: [op], proposals: [], flags: [], skipped: [] },
          op,
          dryRun,
        );
        applied++;

        // Po success merge: usuń deleted node IDs z alive set
        if (op.operation === "merge") {
          const merge = op.payload.merge as MergeOp;
          for (const d of merge.deleted) {
            aliveNodeIds.delete(d.id);
          }
        }
      } catch (opErr) {
        failed++;
        const msg = opErr instanceof Error ? opErr.message : String(opErr);
        failedOps.push({ rule, error: msg });
        // Log jako debug (nie error) — przewidywalne race conditions, defensive handler już
        // obsłużył przez DELETE zamiast UPDATE. Widoczne nadal w onLog dla raw log section.
        onLog("info", `  ⚠ ${rule} op race: ${msg.slice(0, 100)}`);
        logDebug(PREFIX, `${rule} op race (expected): ${msg}`);
      }
      if ((applied + failed) % 10 === 0) {
        onLog("info", `  ${applied + failed}/${allOps.length} (${applied} OK, ${failed} race)`);
      }
    }
    if (rewireConvertedToDelete > 0) {
      onLog(
        "info",
        `  Pre-apply: ${rewireConvertedToDelete} rewire ops converted to DELETE (targets already removed by earlier op)`,
      );
    }
    onLog(
      failed > 0 ? "error" : "info",
      `  done — applied ${applied} operations, ${failed} failed`,
    );
    if (failed > 0) {
      onLog(
        "info",
        `  Failed ops: ${failedOps.slice(0, 5).map((f) => `${f.rule}(${f.error.slice(0, 60)})`).join("; ")}`,
      );
    }

    // 7. Re-audit (tylko gdy !dryRun) + compute delta
    let scoreAfter: number | null = null;
    let l2DetAfter: number | null = null;
    let crossSeedBridgesAfter: number | null = null;

    if (!dryRun && allOps.length > 0) {
      onLog("stage", "▶ Re-audit (skipLLM:true)");
      try {
        const auditRes = await auditMapFull(mapId, { skipLLM: true });
        if (auditRes.report) {
          scoreAfter = auditRes.report.meta.total_score ?? null;
          const l2 = (auditRes.report.layers.L2_deterministic as {
            score?: number;
            cross_seed_bridges?: number;
          }) || {};
          l2DetAfter = l2.score ?? null;
          crossSeedBridgesAfter = l2.cross_seed_bridges ?? null;
          onLog(
            "success",
            `  Re-audit done: score ${state.scoreBefore ?? "?"} → ${scoreAfter ?? "?"}`,
          );
        }
      } catch (e) {
        logError(PREFIX, `re-audit failed: ${(e as Error).message}`);
        onLog("error", `Re-audit failed: ${(e as Error).message}`);
      }
    }

    // 8. Build RepairReport — liczymy FAKTYCZNIE wykonane (safe filtered) nie planowane
    const duplicatesResolved =
      safeR1.length + safeR2.length;
    const orphansResolved = r6Ops.length; // accepted links reduce orphans

    const executedAt = new Date().toISOString();
    const rollbackUntil = new Date(
      Date.now() + ROLLBACK_WINDOW_HOURS * 3600 * 1000,
    ).toISOString();

    const report: RepairReport = {
      repair_version: "v1",
      executed_at: executedAt,
      dry_run: dryRun,
      map_id: mapId,
      duration_ms: Date.now() - t0,
      auto_applied: {
        // Użyj SAFE filtrowanych list (nie oryginalnych) — raport odzwierciedla faktycznie
        // wykonane operacje po conflict resolution w §5.5/§5.6.
        R1_same_seed_merges: safeR1.map(
          (op) => op.payload.merge as MergeOp,
        ),
        R2_cross_seed_merges: safeR2.map(
          (op) => op.payload.merge as MergeOp,
        ),
        R4_relations_cleaned: collectR4Deleted(r4),
        R7_seeds_demoted: collectR7Demoted({
          rule: "R7",
          operations: safeR7,
          proposals: [],
          flags: [],
          skipped: [],
        } as RepairRuleResult),
      },
      review_queue: {
        R3_proposed_moves: r3.proposals as MoveProposal[],
        R6_proposed_relations: r6.proposals as RelationProposal[],
      },
      flagged: {
        R5_suspicious_slugs: r5.flags,
      },
      skipped: {
        has_wp_post: [
          ...r1.skipped.filter((s) => s.reason.startsWith("group_has_wp_post")),
          ...r2.skipped.filter((s) => s.reason.startsWith("group_has_wp_post")),
        ],
        other: [],
      },
      re_audit_delta: {
        score_before: state.scoreBefore,
        score_after: scoreAfter,
        l2_det_before: state.l2DetBefore,
        l2_det_after: l2DetAfter,
        duplicates_resolved: duplicatesResolved,
        orphans_resolved: orphansResolved,
        cross_seed_bridges_before: state.crossSeedBridgesBefore,
        cross_seed_bridges_after: crossSeedBridgesAfter,
        cross_seed_bridges_delta:
          state.crossSeedBridgesBefore !== null &&
          crossSeedBridgesAfter !== null
            ? crossSeedBridgesAfter - state.crossSeedBridgesBefore
            : null,
      },
      rollback_available_until: rollbackUntil,
    };

    // Dodaj accepted proposals info (żeby UI widziało co było accepted)
    if (acceptedR3Props.length > 0 || acceptedR6Props.length > 0) {
      logDebug(PREFIX, "accepted proposals", {
        r3: acceptedR3Props.length,
        r6: acceptedR6Props.length,
      });
    }

    // 9. UPDATE thematic_maps
    if (!dryRun) {
      await db
        .from("thematic_maps")
        .update({
          repair_report: report,
          last_repaired_at: executedAt,
          repair_status: "completed",
          repair_rollback_available_until: rollbackUntil,
        })
        .eq("id", mapId);
    }

    onLog(
      "stage",
      `✓ Repair done ${((Date.now() - t0) / 1000).toFixed(1)}s · ` +
        `R1=${r1.operations.length} R2=${r2.operations.length} R4=${collectR4Deleted(r4).deleted_count} R7=${r7.operations.length} ` +
        `| review: R3=${r3.proposals.length} R6=${r6.proposals.length} | flags: R5=${r5.flags.length} ` +
        `| delta score: ${state.scoreBefore ?? "?"}→${scoreAfter ?? "?"}`,
      {
        r1: r1.operations.length,
        r2: r2.operations.length,
        r4_cleaned: collectR4Deleted(r4).deleted_count,
        r7: r7.operations.length,
        r3_proposals: r3.proposals.length,
        r6_proposals: r6.proposals.length,
        r5_flags: r5.flags.length,
        score_before: state.scoreBefore,
        score_after: scoreAfter,
        duration_ms: Date.now() - t0,
      },
    );

    return { success: true, mapId, report };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    logError(PREFIX, `repair failed: ${msg}`);
    onLog("error", `Repair failed: ${msg}`);

    if (!dryRun) {
      await db
        .from("thematic_maps")
        .update({ repair_status: "failed" })
        .eq("id", mapId);
    }
    throw err;
  }
}
